import { createContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '../../../shared/services/users/usersServices';

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  phone: string;
  cpf_or_rg: string;
  birth_date: string;
  password?: string; // Só na criação
}

interface UserContextData {
  // Listagem
  users: User[];
  isLoading: boolean;
  loading: boolean;
  error: string | null;
  page: number;
  limit: number;
  total: number;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  handleCreate: () => void;
  handleView: (user: User) => void;
  handleCloseError: () => void;

  // Modal de Delete
  isDeleteModalOpen: boolean;
  selectedUser: User | null;
  openDeleteModal: (user: User) => void;
  closeDeleteModal: () => void;
  handleDeleteUser: () => void;
  isDeleting: boolean;

  // Navegação
  navigateToEdit: (id: string) => void;

  // Form
  userData: User | null;
  isDataLoading: boolean;
  createUser: (data: any) => Promise<void>;
  updateUser: (id: string, data: any) => Promise<void>;
  isSubmitting: boolean;
  getUserById: (id: string) => User;
  setError: (error: string | null) => void;
}

interface UserProviderProps {
  children: ReactNode;
}

export const UserContext = createContext<UserContextData>({} as UserContextData);

export function UserProvider({ children }: UserProviderProps) {

  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(15);

  // Query para listar usuários (organizers) com paginação
  interface UsersResponse {
    users: User[];
    total: number;
    page: number;
    limit: number;
  }

  const { data: response = { users: [], total: 0, page: 1, limit: 15 } as UsersResponse, isLoading } = useQuery<UsersResponse>({
    queryKey: ['users', 'organizer', page, limit],
    queryFn: async () => {
      const res = await userService.listByRole('organizer', { page, limit });
      return res.data as UsersResponse;
    },
  });

  const total = response?.total ?? 0;

  const [userData, setUserData] = useState<User | null>(null);
  const [isDataLoading, setIsDataLoading] = useState(false);



  // Estado do modal de delete
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Mutation para deletar usuário
  const deleteMutation = useMutation({
    mutationFn: (id: string) => userService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      closeDeleteModal();
      navigate('/users');
    }
  });

  // Paginação
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  // Handlers para listagem
  const handleCreate = () => {
    navigate('/users/new');
  };

  const handleView = (user: User) => {
    navigate(`/users/${user.id}`);
  };

  const handleCloseError = () => {
    setError(null);
  };

  // Handlers para modal de delete
  const openDeleteModal = (user: User) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedUser(null);
  };

  const handleDeleteUser = () => {
    if (selectedUser?.id) {
      deleteMutation.mutate(selectedUser.id);
    }
  };

  // Handler para navegação
  const navigateToEdit = (id: string) => {
    navigate(`/users/${id}/edit`);
  };

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      return await userService.create({
        name: data.name,
        email: data.email,
        password: data.password,
        phone: data.phone,
        cpf_or_rg: data.cpf_or_rg,
        birth_date: data.birth_date,
        role: data.role,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: any) => {
      let errorMessage = 'Erro ao criar usuário';
      if (typeof error === 'object' && error !== null) {
        const backendError =
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message;

        if (backendError === 'User with this email already exists') {
          errorMessage = 'Já existe um usuário com este e-mail.';
        } else {
          errorMessage = backendError || errorMessage;
        }
        setError(errorMessage);
      } else {
        setError(errorMessage);
      }
      throw error;
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      await userService.update(id, {
        name: data.name,
        email: data.email,
        phone: data.phone,
        cpf_or_rg: data.cpf_or_rg,
        birth_date: data.birth_date,
        role: data.role,
      });
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['users', 'organizer'] });
      queryClient.invalidateQueries({ queryKey: ['user', id] });
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao atualizar usuário';
      setError(errorMessage);
      throw error;
    }
  });
  useEffect(() => {
    if (id) {
      setIsDataLoading(true);
      userService.get(id)
        .then(res => setUserData(res.data.user))
        .catch(() => setUserData(null))
        .finally(() => setIsDataLoading(false));
    } else {
      setUserData(null);
      setIsDataLoading(false);
    }
  }, [id, updateMutation?.isSuccess]);
  const createUser = async (data: any) => {
    await createMutation.mutateAsync(data);
  };

  const updateUser = async (id: string, data: any) => {
    await updateMutation.mutateAsync({ id, data });
  };

  const getUserById = (id: string) => {
    const currentUsers = response.users || [];
    const user = currentUsers.find((u: any) => u.id === id);
    if (!user) {
      throw new Error('Usuário não encontrado');
    }
    return user;
  };

  return (
    <UserContext.Provider
      value={{
        // Listagem
        users: response.users || [],
        isLoading,
        loading: createMutation.isPending || updateMutation.isPending,
        error,
        handleCreate,
        handleView,
        handleCloseError,
        page,
        limit,
        total,
        setPage: handlePageChange,
        setLimit: handleLimitChange,

        // Modal de Delete
        isDeleteModalOpen,
        selectedUser,
        openDeleteModal,
        closeDeleteModal,
        handleDeleteUser,
        isDeleting: deleteMutation.isPending,

        // Navegação
        navigateToEdit,

        // Form
        userData,
        isDataLoading,
        createUser,
        updateUser,
        isSubmitting: createMutation.isPending || updateMutation.isPending,
        getUserById,
        setError,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}